import type { ConfiguredAddOn, ConfiguredService } from '@/features/service-plans';
import type { DateKey } from '../utils/dateKey';
import { wholeMonthsBetween } from '../utils/dateKey';
import { toDueBasketItems } from '../utils/dueBasketItems';
import { toMinorUnits } from '../utils/money';

/** Latest due date across positively-priced dated items in both lists, or `null` when there are none.
 * Items and inclusion match `buildCollectionTargets`; `planTerm.test.ts` pins that agreement. */
export const latestPricedDueDate = (
    services: ConfiguredService[],
    addOns: ConfiguredAddOn[],
): DateKey | null => {
    const items = toDueBasketItems(services, addOns);

    let latest: DateKey | null = null;
    for (const item of items) {
        const grossMinor = toMinorUnits(item.grossPrice);
        if (!(grossMinor > 0)) {
            continue;
        }
        const dateKey = item.dueDate;
        if (dateKey === null) {
            continue;
        }
        if (latest === null || dateKey > latest) {
            latest = dateKey;
        }
    }
    return latest;
};

/**
 * Whole months from `anchorDateKey` to the latest priced due date, FLOORED
 * (elemis maps dates to periods by flooring — contract §8 B2).
 *
 * `undefined` when no term can be derived: an empty basket, a basket with no
 * positively-priced dated item, or an anchor/date pair `wholeMonthsBetween`
 * cannot parse (it returns `NaN`). NEVER guesses (Power of Ten rule 5).
 *
 * MAY return zero or a negative number — every dated item falling within the
 * next month, or in the past, is a real basket state. The caller decides what
 * to do with it; this function does not clamp.
 *
 * The anchor is a parameter, not a clock read, for the same reason
 * `alignCollectionTargets` takes one (#730 D1): the caller owns "when now
 * is", this module stays pure and trivially testable, and one clock read
 * serves both the item-based and even-monthly families.
 */
export const derivePlanTermMonths = (
    services: ConfiguredService[],
    addOns: ConfiguredAddOn[],
    anchorDateKey: DateKey,
): number | undefined => {
    const latest = latestPricedDueDate(services, addOns);
    if (latest === null) {
        return undefined;
    }
    const months = wholeMonthsBetween(anchorDateKey, latest);
    // `wholeMonthsBetween` returns NaN for an unparsable key. Decline rather
    // than let a NaN count leak into a request.
    return Number.isInteger(months) ? months : undefined;
};
